import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);

const CustomerPortal = ({ projectId }) => {
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setProject(null);
    setError(false);
    api
      .get(`/projects/${projectId}`)
      .then((r) => !cancel && setProject(r.data))
      .catch(() => {
        if (!cancel) {
          setError(true);
          toast.error("Could not load your project");
        }
      });
    return () => {
      cancel = true;
    };
  }, [projectId]);

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6" data-testid="customer-portal-error">
        <p className="text-center text-sm text-white/50">This project is unavailable. Please contact tech support.</p>
      </div>
    );
  }

  if (!project) {
    return <p className="px-6 py-10 text-sm text-white/50" data-testid="customer-portal-loading">Loading your project…</p>;
  }

  const lines = project.contract_lines || [];
  const contractTotal = lines.reduce((a, l) => a + (Number(l.contract_value) || 0), 0);
  const completedTotal = lines.reduce((a, l) => a + (Number(l.value_to_date) || 0), 0);
  const progress = contractTotal > 0 ? Math.min(100, Math.round((completedTotal / contractTotal) * 100)) : 0;

  const facts = [
    ["Status", project.status],
    ["Location", project.location],
    ["PO", project.po],
    ["Progress Claim #", project.progress_claim_no],
    ["Date", project.date],
  ].filter(([, v]) => v);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12" data-testid="customer-portal">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8fa4f0]">Your Project</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="customer-project-name">
        {project.name}
      </h1>

      {/* progress */}
      <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="customer-progress">
        <div className="flex items-end justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Overall Progress</span>
          <span className="font-display text-2xl font-bold text-white">{progress}%</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-[#1a3fd1] transition-[width] duration-700" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">Contract Value</p>
            <p className="font-semibold text-white">{money(contractTotal)}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">Completed to Date</p>
            <p className="font-semibold text-white">{money(completedTotal)}</p>
          </div>
        </div>
      </div>

      {/* facts */}
      {facts.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="customer-facts">
          {facts.map(([label, value]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">{label}</p>
              <p className="mt-0.5 truncate text-sm font-medium text-white">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* contract line items */}
      <div className="mt-8">
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Scope of Work</h2>
        <div className="overflow-hidden rounded-xl border border-white/10" data-testid="customer-scope">
          {lines.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-white/40">No scope items listed yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.04] text-left font-mono text-[10px] uppercase tracking-wider text-white/40">
                  <th className="px-4 py-2.5">Description</th>
                  <th className="px-4 py-2.5 text-right">Value</th>
                  <th className="px-4 py-2.5 text-right">Complete</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const linePct =
                    l.percent_to_date !== null && l.percent_to_date !== undefined && l.percent_to_date !== ""
                      ? `${Math.round(Number(l.percent_to_date) * 100)}%`
                      : "—";
                  return (
                    <tr key={i} className="border-t border-white/5" data-testid="customer-scope-row">
                      <td className="px-4 py-2.5 text-white/80">{l.description || l.code || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-white/70">{money(l.contract_value)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#8fa4f0]">{linePct}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="mt-10 text-center font-mono text-[11px] text-white/30">
        Questions about your project? Call us at{" "}
        <a href="tel:7055626494" className="text-[#8fa4f0] hover:text-white">705-562-6494</a>
      </p>
    </div>
  );
};

export default CustomerPortal;
